import type { ReactNode } from 'react';
import { RowLink } from '@/components/DataFigures';
import { StatusDot } from '@/components/StatusDot';
import { Skeleton } from '@/components/Skeleton';
import type { StatusTone } from '@/theme/statusTone';
import { cn } from '@/lib/utils';
import { TILE_SCROLL } from '@/lib/dashboardGrid';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  THE TILE-ONLY PARTS OF A NUMBER. [P6a]
 *
 *  ⚠️ [ANALYTICS §5] `FigureList`, `RankedList` AND `RowLink` MOVED TO `components/DataFigures.tsx`
 *  when the Usage Analytics page became their second user, and R1 and R2 — the two rules every
 *  graphic here obeys — moved with them and are stated there. What is left is about a TILE: a
 *  big number whose tone is a STATUS, the skeletons a tile loads with, and the one tile body
 *  that scrolls.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

/**
 * One big figure.
 *
 * ⚠️ `tabular-nums`, so a number that changes on a refetch does not reflow its own tile.
 * ⚠️ AND THE TONE FOLLOWS THE NUMBER, NEVER THE TILE. A permanently red card stops being
 * read within a week, and colour must never say "problem" about a good number.
 */
export const BigNumber = ({
  value,
  unit,
  tone,
  label,
}: {
  value: number;
  unit: string;
  tone: StatusTone;
  label: string;
}) => (
  <div className="flex flex-col gap-1.5">
    <span className="text-4xl font-semibold tabular-nums text-foreground">{value.toLocaleString('en-IN')}</span>
    <span className="text-xs text-muted-foreground">{unit}</span>
    <StatusDot tone={tone} label={label} />
  </div>
);

export const BigNumberSkeleton = () => (
  <div className="flex flex-col gap-2">
    <Skeleton className="h-9 w-24" />
    <Skeleton className="h-3 w-16" />
  </div>
);

export const ListSkeleton = ({ rows = 4 }: { rows?: number }) => (
  <div className="flex flex-col gap-2">
    {Array.from({ length: rows }, (_, index) => (
      <Skeleton key={index} className="h-4 w-full" />
    ))}
  </div>
);

/** What a tile with a real answer of "nothing" says. Never a blank card. */
export const TileEmpty = ({ children }: { children: ReactNode }) => (
  <p className="text-sm text-muted-foreground">{children}</p>
);

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [C1e §1a] THE DUTY LIST — the only tile body in this app that scrolls.
 *
 *  ⚠️ THE SCROLL IDIOM LIVES HERE AND NOWHERE ELSE, so `TILE_SCROLL`'s two rules (a literal
 *  class, and NO `overscroll-contain`) have exactly one call site to be got wrong at. Read
 *  that constant's header before touching this: the missing `overscroll-contain` is the
 *  decision, not an omission.
 *
 *  ⚠️ AND THIS IS THE ONE RENDERER THAT DOES NOT `.slice()` WHAT IT IS GIVEN. Every other
 *  tile bounds its height by showing four to six rows; this one bounds it with a `max-h`, and
 *  the twenty rows the server sent are what the operator scrolls through. "There are more"
 *  is stated OUTSIDE the scroller by the caller, so it cannot scroll away.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const DutyRows = ({
  rows,
}: {
  rows: readonly {
    key: string;
    /** The row's own name. Truncates — it is the cell reachable another way ([D4.4 §7a]). */
    dutyPoint: string;
    /** ⚠️ [C3c.2] Where the name goes. Omit and it stays plain text — see `RowLink`. */
    href?: string;
    when: string;
    /**
     * ⚠️ [amended C1g §1e] `detail`, RENAMED FROM `addressees` NOW THAT IT HAS TWO CALLERS.
     *
     * `ARRANGEMENTS_TODAY` puts the addressee short codes here; `ORDERS_TO_FILL` puts "3 of 12
     * named". A prop named after ONE caller's meaning is how the second caller's row reads as
     * a lie in source while rendering correctly — and this component is otherwise exactly the
     * shape both want, so a second list primitive would be duplication for a word.
     */
    detail: ReactNode;
    state: { tone: StatusTone; label: string };
    action?: ReactNode;
  }[];
}) => (
  <div className={cn('relative z-10', TILE_SCROLL)} data-testid="duty-rows">
    <ul className="divide-y divide-border">
      {rows.map((row) => (
        <li key={row.key} data-row={row.key} className="flex items-center gap-3 py-1.5">
          {/* ⚠️ [D4.4 §7a] The one cell whose full text is reachable another way, so it truncates. */}
          <RowLink href={row.href} title={row.dutyPoint}>
            {row.dutyPoint}
          </RowLink>
          <span className="shrink-0 text-xs text-muted-foreground">{row.when}</span>
          <span className="min-w-0 max-w-[12rem] shrink-0 truncate text-xs text-muted-foreground">{row.detail}</span>
          <StatusDot tone={row.state.tone} label={row.state.label} />
          {row.action}
        </li>
      ))}
    </ul>
  </div>
);
